import { useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { Modal } from "@/components/Modals/Modal";
import { useSendScheduleNowMutation } from "@/pages/Private/redux/schedule/schedule.api";
import { ListPersonSchedule } from "@/pages/Private/pages/List/schema/list";
import { useSnackBar } from "@/utils/useSnackbar";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { listOtherSenders, senderConfirmationQuestion } from "./senderConfirmation.util";

/**
 * Confirm-and-send-now flow for one scheduled manual message (PREC-3103), the sibling of
 * `useCancelScheduleModal` and `usePauseScheduleModal`. Self-contained the same way: it owns its
 * snackbar and is rendered by the bubble's controls.
 *
 * The confirmation names what the user gives up — the send time they picked — and, for a paused
 * message, that sending it now also ends the pause, so neither happens as a surprise. When the
 * message goes out as someone other than the current user it also asks the composer's question
 * ("Send this message as Anna Müller (anna@example.com)?", PREC-4312).
 */
export const useSendNowScheduleModal = () => {
	const { t } = useTranslation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	// The schedule pending confirmation; null when the modal is closed.
	const [schedule, setSchedule] = useState<ListPersonSchedule | null>(null);
	const [channel, setChannel] = useState<"email" | "linkedin">("email");
	const currentUserId = useAppSelector(authSelector)?.user?.id;
	const { data: sendingUsers } = useGetSendersUsersQuery();

	const senderQuestion = useMemo(() => {
		const sender = schedule && {
			userId: schedule.userId,
			type: channel,
			emailAddress: schedule.email?.email,
		};
		const others = listOtherSenders([sender], currentUserId, sendingUsers?.data);

		return others.length ? senderConfirmationQuestion(others, 1) : null;
	}, [schedule, channel, currentUserId, sendingUsers?.data]);
	const [sendScheduleNow, { isLoading }] = useSendScheduleNowMutation();

	const handleConfirm = useCallback(async () => {
		if (!schedule) {
			return;
		}

		try {
			await sendScheduleNow(schedule.id).unwrap();
			handleOpenSnackbar(t("outbox.sendNowSchedule.success"));
		} catch {
			handleOpenSnackbar(t("outbox.sendNowSchedule.error"));
		} finally {
			setSchedule(null);
		}
	}, [schedule, sendScheduleNow, handleOpenSnackbar, t]);

	const confirmation = (): string => {
		if (!schedule?.active) {
			return t("outbox.sendNowSchedule.confirmationPaused");
		}

		return schedule.scheduledFor
			? t("outbox.sendNowSchedule.confirmation", {
					date: format(new Date(schedule.scheduledFor), "HH:mm dd.MM.yyyy"),
				})
			: t("outbox.sendNowSchedule.confirmationNow");
	};

	const renderSendNowScheduleModal = () => (
		<>
			{schedule !== null && (
				<Modal
					handleClose={() => setSchedule(null)}
					handleSave={handleConfirm}
					isLoading={isLoading}
					isOpened={schedule !== null}
					size="xs"
					submitButtonText={t("outbox.sendNowSchedule.action")}
					title={t("outbox.sendNowSchedule.title")}
				>
					<div className="text-sm text-gray-700" data-testid="send-now-schedule-confirmation">
						{confirmation()}
					</div>
					{senderQuestion && (
						<p
							className="mt-2 text-sm font-semibold text-text"
							data-testid="sender-confirm-question"
						>
							{t(senderQuestion.key, senderQuestion.values)}
						</p>
					)}
				</Modal>
			)}
			{renderMessageSnackbar()}
		</>
	);

	// The channel says how the sender is named: by mailbox for a mail, as LinkedIn otherwise.
	const requestSendNow = useCallback(
		(next: ListPersonSchedule, nextChannel: "email" | "linkedin" = "email") => {
			setChannel(nextChannel);
			setSchedule(next);
		},
		[]
	);

	return { requestSendNow, renderSendNowScheduleModal };
};
